import {
  Box,
  Flex,
  FormatNumber,
  HStack,
  Heading,
  Image,
  Text,
} from '@chakra-ui/react';
import {
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogRoot,
} from '~/components/ui/dialog';
import { useI18nLn } from '~/hooks/store';
import { translateCountryCodeToLocaleText } from '~/src/services/i18n/country';
import { DeliveryBundle } from '~/types/resources/deliveryBundle';
import { DeliveryInfo } from '~/types/resources/deliveryInfo';
import { EventRemainDeliveryCount } from './EventRemainDeliveryCount';

import type { GachaDetail } from '~/types/resources/gachaDetail';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  deliveryInfo: DeliveryInfo | null;
  deliveryBundles: DeliveryBundle[];
  shippingSpecs: GachaDetail['shipping_specs'];
};

export const EventDeliveryInfoModal: React.FC<Props> = (props: Props) => {
  return (
    <DialogRoot open={props.isOpen} onInteractOutside={props.onClose}>
      <DialogBackdrop />
      <DialogContent mx={2} my={0} alignSelf="center">
        <DialogHeader pt="8px" pb="20px" px="8px">
          <Flex flexDir="row" w="full" align="end">
            <Box w="36px" />
            <Text fontSize="13px" w="full" textAlign="center">
              配送料について
            </Text>
            <Box w="8px" />
            <Image
              src="/assets/icon/close.svg"
              alt="icon"
              h="28px"
              w="28px"
              onClick={props.onClose}
            />
          </Flex>
        </DialogHeader>
        <DialogBody overflowY="auto">
          <Flex flexDir="column" maxH="50vh">
            {props.shippingSpecs.map((spec, index) => {
              return (
                <EventDeliveryInfoCountryItem
                  key={index}
                  spec={spec}
                  deliveryBundles={props.deliveryBundles}
                  deliveryInfo={props.deliveryInfo}
                />
              );
            })}
          </Flex>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};

export const EventDeliveryInfoCountryItem = (props: {
  spec: GachaDetail['shipping_specs'][number];
  deliveryBundles: DeliveryBundle[];
  deliveryInfo: DeliveryInfo | null;
}) => {
  const { spec } = props;
  const { t, dictionary } = useI18nLn();

  return (
    <>
      <Box>
        <Heading fontSize="md" py={1}>
          {translateCountryCodeToLocaleText({
            countryCode: spec.country,
            dictionary,
          })}
        </Heading>
        <Flex flexDir="row" justify="left" align="baseline" w="full">
          <Text fontSize="13px">配送料</Text>
          <HStack gap={0} px={1} justifyContent="center" alignItems="center">
            {spec.bundle_capacity != 0 && (
              <Text fontSize="13px" fontWeight="bold">
                {t('components.shipments.about_bundle_capacity', {
                  amount: spec.bundle_capacity,
                })}
              </Text>
            )}
            <HStack gap={0.5} px={1} justifyContent="center">
              <Text fontSize="13px">&yen;</Text>
              <Text fontSize="18px" fontWeight="bold">
                <FormatNumber value={spec.charge} />
              </Text>
            </HStack>
          </HStack>
          <Text fontSize="13px">({t('global.common.tax.included')})</Text>
        </Flex>
        <Box h="8px" />
        <EventRemainDeliveryCount
          deliveryBundles={props.deliveryBundles}
          deliveryInfo={props.deliveryInfo}
          country={spec.country}
        />
        <Text textAlign="left" fontSize="13px" fontWeight="bold">
          {t('components.shipments.about_fee_detail')}
        </Text>
        <Text textAlign="left" fontSize="11px" whiteSpace="pre-line">
          {spec.exposition_text}
        </Text>
        <Box h="8px" />
        <Text textAlign="left" fontSize="13px" fontWeight="bold">
          {t('components.shipments.period')}
        </Text>
        <Text textAlign="left" fontSize="11px" whiteSpace="pre-line">
          {spec.shipping_schedule}
        </Text>
      </Box>
      <Box divideX="1px" my="8px" _last={{ visibility: 'hidden' }} />
    </>
  );
};
